import { useRef, useEffect, useState } from 'react';

// Usage: const [ref, style, visible] = useFadeInParallax();
export default function useFadeInParallax() {
  const ref = useRef();
  const [visible, setVisible] = useState(false);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Fade in
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );
    observer.observe(node);

    // Parallax/scale effect
    const handleScroll = () => {
      const rect = node.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const center = rect.top + rect.height / 2;
      const viewportCenter = windowHeight / 2;
      const distance = Math.abs(center - viewportCenter);
      const maxDistance = windowHeight / 2 + rect.height / 2;
      // Scale up to 1.06 at center, 1 at edges
      const scaleValue = 1 + 0.06 * (1 - Math.min(distance / maxDistance, 1));
      setScale(scaleValue);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    handleScroll();
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  const style = {
    opacity: visible ? 1 : 0,
    transform: `scale(${scale})`,
    transition: 'opacity 0.7s cubic-bezier(0.4,0,0.2,1), transform 0.5s cubic-bezier(0.4,0,0.2,1)'
  };

  return [ref, style, visible];
}
